import React, { useState, useEffect } from "react";
import { Header } from "../../Header";
import CircularProgress from "@mui/material/CircularProgress";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import InventoryIcon from "@mui/icons-material/Inventory";
import DownloadIcon from "@mui/icons-material/Download";
import SendIcon from "@mui/icons-material/Send";
import AssessmentIcon from "@mui/icons-material/Assessment";
import { local_signage, local_url } from "../../Const";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';


// Format date as YYYYMMDD for API
const formatDateForAPI = (date) => {
  if (!date) return "";
  if (typeof date === 'string') {
    const [year, month, day] = date.split('-');
    return `${year}${month}${day}`;
  }
  // If it's a dayjs object
  return date.format('YYYYMMDD');
};

export const HorecaAccumulated = () => {
  const today = dayjs();
  const sixtyDaysAgo = today.subtract(0, 'day');

  const [ledgerData, setLedgerData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fromDate, setFromDate] = useState(sixtyDaysAgo);
  const [toDate, setToDate] = useState(today);
  const [sfAreas, setSfAreas] = useState([]);
  const [selectedSfAreaId, setSelectedSfAreaId] = useState("");
  const [fetchingData, setFetchingData] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  
  // Date validation state
  const [isDateRangeValid, setIsDateRangeValid] = useState(true);
  const [hasSubmittedData, setHasSubmittedData] = useState(false);
  
  // Accumulated items state
  const [accumulatedItems, setAccumulatedItems] = useState([]);

  // Enhanced Styles Object
  const styles = {
    container: {
      width: '100vw',
      height: '90vh',
      overflow: 'auto',
      backgroundColor: '#f8fafc',
      fontFamily: '"Inter", "Segoe UI", Tahoma, Geneva, Verdana, sans-serif',
      color: '#1e293b',
      padding: '8px'
    },
    errorMessage: {
      backgroundColor: '#fee2e2',
      border: '1px solid #fecaca',
      color: '#dc2626',
      padding: '6px 10px',
      borderRadius: '4px',
      marginBottom: '8px',
      fontSize: '12px',
      fontWeight: '500',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
    },
    inputContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      marginBottom: '10px',
      padding: '8px',
      backgroundColor: '#ffffff',
      borderRadius: '6px',
      boxShadow: '0 2px 4px -1px rgba(0, 0, 0, 0.1)',
      border: '1px solid #e2e8f0',
      flexWrap: 'wrap'
    },
    dropdown: {
      padding: '6px 8px',
      border: '1px solid #e2e8f0',
      borderRadius: '4px',
      fontSize: '12px',
      backgroundColor: '#ffffff',
      color: '#374151',
      cursor: 'pointer',
      minWidth: '140px',
      transition: 'all 0.2s ease-in-out',
      outline: 'none',
      appearance: 'none',
      backgroundImage: 'url("data:image/svg+xml,%3csvg xmlns=\'http://www.w3.org/2000/svg\' fill=\'none\' viewBox=\'0 0 20 20\'%3e%3cpath stroke=\'%236b7280\' stroke-linecap=\'round\' stroke-linejoin=\'round\' stroke-width=\'1.5\' d=\'M6 8l4 4 4-4\'/%3e%3c/svg%3e")',
      backgroundPosition: 'right 8px center',
      backgroundRepeat: 'no-repeat',
      backgroundSize: '12px',
      paddingRight: '24px'
    },
    datePickerContainer: {
      position: 'relative',
      display: 'flex',
      flexDirection: 'column'
    },
    submitButton: {
      padding: '6px 12px',
      backgroundColor: '#3b82f6',
      color: '#ffffff',
      border: 'none',
      borderRadius: '4px',
      fontSize: '12px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.2s ease-in-out',
      boxShadow: '0 2px 4px -1px rgba(59, 130, 246, 0.3)',
      outline: 'none',
      minWidth: '80px'
    },
    exportButton: {
      padding: '6px 12px',
      backgroundColor: '#10b981',
      color: '#ffffff',
      border: 'none',
      borderRadius: '4px',
      fontSize: '12px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.2s ease-in-out',
      boxShadow: '0 2px 4px -1px rgba(16, 185, 129, 0.3)',
      outline: 'none',
      minWidth: '90px'
    },
    viewToggleContainer: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: '8px',
      marginBottom: '16px',
      padding: '12px 16px',
      backgroundColor: '#ffffff',
      borderRadius: '8px',
      boxShadow: '0 2px 4px -1px rgba(0, 0, 0, 0.1)',
      border: '1px solid #e2e8f0',
    },
    dataCount: {
      fontSize: '11px',
      fontWeight: '600',
      color: '#6366f1',
      backgroundColor: '#eef2ff',
      padding: '4px 8px',
      borderRadius: '12px',
      border: '1px solid #c7d2fe'
    },
    loaderContainer: {
      position: 'fixed',
      top: '0',
      left: '0',
      width: '100%',
      height: '100%',
      backgroundColor: 'rgba(248, 250, 252, 0.95)',
      backdropFilter: 'blur(4px)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: '1000'
    },
    loaderCard: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '32px 40px',
      backgroundColor: '#ffffff',
      borderRadius: '12px',
      boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
      border: '1px solid #e2e8f0',
      minWidth: '300px',
      textAlign: 'center'
    },
    loaderSpinner: {
      width: '48px',
      height: '48px',
      marginBottom: '16px'
    },
    loaderText: {
      fontSize: '16px',
      fontWeight: '600',
      color: '#374151',
      marginBottom: '8px'
    },
    loaderSubtext: {
      fontSize: '14px',
      fontWeight: '400',
      color: '#6b7280',
      lineHeight: '1.5'
    },
    tableContainer: {
      backgroundColor: '#ffffff',
      borderRadius: '6px',
      overflow: 'hidden',
      boxShadow: '0 2px 4px -1px rgba(0, 0, 0, 0.1)',
      border: '1px solid #e2e8f0'
    },
    tableScroll: {
      overflowX: 'auto',
      overflowY: 'auto',
      maxHeight: '61vh'
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      fontSize: '13px',
      backgroundColor: '#ffffff'
    },
    tableHeader: {
      backgroundColor: '#f8fafc',
      color: '#374151',
      fontWeight: '600',
      fontSize: '12px',
      textTransform: 'uppercase',
      letterSpacing: '0.3px',
      position: 'sticky',
      top: '0',
      zIndex: '10'
    },
    tableHeaderCell: {
      padding: '8px 6px',
      textAlign: 'left',
      borderBottom: '1px solid #e2e8f0',
      borderRight: '1px solid #f1f5f9',
      whiteSpace: 'nowrap'
    },
    tableRow: {
      transition: 'background-color 0.2s ease-in-out',
      borderBottom: '1px solid #f1f5f9'
    },
    tableCell: {
      padding: '7px 5px',
      textAlign: 'left',
      borderRight: '1px solid #f8fafc',
      wordWrap: 'break-word',
      maxWidth: '160px',
      fontSize: '12px'
    },
    noData: {
      textAlign: 'center',
      padding: '40px 20px',
      color: '#6b7280',
      fontSize: '16px',
      fontWeight: '500',
      fontStyle: 'italic'
    }
  };

  const userId = localStorage.getItem("userId");
  const formattedFromDateForAPI = fromDate ? formatDateForAPI(fromDate) : "";
  const formattedToDateForAPI = toDate ? formatDateForAPI(toDate) : "";

  // Date validation function for 75 days maximum
  const validateDateRange = (startDate, endDate) => {
    if (!startDate || !endDate) return true; // Allow empty dates
    
    const daysDiff = endDate.diff(startDate, 'day');
    return daysDiff >= 0 && daysDiff <= 75; // Maximum 75 days range
  };

  // Update date range validation when dates change
  useEffect(() => {
    const isValid = validateDateRange(fromDate, toDate);
    setIsDateRangeValid(isValid);
  }, [fromDate, toDate]);

  // Process data to accumulate items by ItemID
  const processAccumulatedItems = (data) => {
    const itemAccumulation = {};
    const itemMeta = {}; // To store AreaName and ScheduleDate for each itemId

    data.forEach(order => {
      if (order.itemline && order.itemline.length > 0) {
        order.itemline.forEach(item => {
          const itemId = item.ItemID;
          if (!itemAccumulation[itemId]) {
            itemAccumulation[itemId] = {
              itemId,
              itemName: item.ItemName,
              totalQty: 0,
              totalAmount: 0,
              rate: item.Rate || 0,
              orderCount: 0
            };
          }
          itemAccumulation[itemId].totalQty += item.Qty || 0;
          itemAccumulation[itemId].totalAmount += (item.Qty || 0) * (item.Rate || 0);
          itemAccumulation[itemId].orderCount += 1;

          // Store meta for grouping
          if (!itemMeta[itemId]) {
            itemMeta[itemId] = [];
          }
          itemMeta[itemId].push({
            scheduleDate: order.DeliveryScheduleDate,
            areaName: order.AreaName
          });
        });
      }
    });

    // Attach meta to each item
    const result = Object.values(itemAccumulation).map(item => ({
      ...item,
      meta: itemMeta[item.itemId]
    }));

    setAccumulatedItems(result);
  };

  // Group accumulated items by ScheduleDate and AreaName
  const groupByScheduleDateAndArea = (data) => {
    const grouped = {};
    data.forEach(order => {
      const scheduleDate = order.DeliveryScheduleDate ? dayjs(order.DeliveryScheduleDate).format('DD-MM-YYYY') : 'Unknown Date';
      const areaName = order.AreaName || 'Unknown Area';
      const key = `${scheduleDate}__${areaName}`;
      if (!grouped[key]) {
        grouped[key] = {
          scheduleDate,
          areaName,
          orders: []
        };
      }
      grouped[key].orders.push(order);
    });
    return Object.values(grouped);
  };

  const API_URL = `${local_signage}/api/v1/ordermovementreport?ManagerId=ALL&TransTypeID=SALEORDER&TranSubTypeID=ALL&ChannelId=ALL&AreaId=${selectedSfAreaId || 'ALL'}&FromDate=${formattedFromDateForAPI}&ToDate=${formattedToDateForAPI}`;

  const fetchLedgerData = async () => {
    setLoading(true);
    setFetchingData(true);
    setError(null);
    setHasSubmittedData(false); // Reset submission flag

    try {
      const token = localStorage.getItem('AuthorizationToken');
      if (!token) {
        setError("Authorization token is missing. Please log in again.");
        localStorage.clear();
        sessionStorage.clear();
        window.location.href = "/login";
        return;
      }

      // Make API call with Authorization header
      const response = await fetch(API_URL, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.status === 401) {
        console.error("Session expired or invalid token.");
        localStorage.clear();
        sessionStorage.clear();
        alert("Session expired. Please log in again.");
        window.location.href = "/login";
        return;
      }

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      // Parse response data
      const responseData = await response.json();
      const data = responseData.BatchBranchMove || [];
      console.log(`Fetched ${data.length} records`);
      
      // Process data to calculate total amount when zero and flatten item data
      const processedData = data.map(order => {
        let totalAmount = order.TotalAmount || 0;
        
        // Calculate total amount if zero
        if (totalAmount === 0 && order.itemline && order.itemline.length > 0) {
          totalAmount = order.itemline.reduce((sum, item) => {
            return sum + ((item.Qty || 0) * (item.Rate || 0));
          }, 0);
        }
        
        return {
          ...order,
          TotalAmount: totalAmount,
          totalAmount: totalAmount
        };
      });
      
      setLedgerData(processedData);
      setFilteredData(processedData);
      
      // Process accumulated items
      processAccumulatedItems(processedData);
      
      setHasSubmittedData(true); // Mark as successfully submitted with data
    } catch (error) {
      console.error("Error fetching ledger data:", error);
      setError("Failed to fetch ledger data. Please try again later.");
    } finally {
      setLoading(false);
      setFetchingData(false);
    }
  };

  const fetchSfAreas = async () => {
    try {
      const response = await fetch(`${local_url}/api/SFArea/${userId}`);
      if (!response.ok) throw new Error("Failed to fetch SF Areas");
      const data = await response.json();
      setSfAreas(data);
    } catch (error) {
      setError("Failed to fetch SF Areas. Please try again later.");
    }
  };

  const handleSfAreaChange = (event) => {
    setSelectedSfAreaId(event.target.value);
    // Clear table data when area changes
    setLedgerData([]);
    setFilteredData([]);
    setAccumulatedItems([]);
    setHasSubmittedData(false);
  };

  // Function to escape CSV values properly
  const escapeCSV = (value) => {
    if (value == null || value === undefined) return '""';
    const stringValue = String(value);
    // If the value contains quotes, commas, or newlines, wrap in quotes and escape quotes
    if (stringValue.includes('"') || stringValue.includes(',') || stringValue.includes('\n')) {
      return `"${stringValue.replace(/"/g, '""')}"`;
    }
    return stringValue;
  };

  const exportToCSV = async () => {
    if (filteredData.length === 0) {
      alert("No data to export");
      return;
    }

    setExportLoading(true);

    try {
      // Headers for export
      const headers = [
        "S No",
        "Schedule Date",
        "Area Name",
        // "Created Date",
        "Item ID",
        "Item Name",
        "Total Qty"
      ];

      // Start with BOM for Excel UTF-8 compatibility
      let csvContent = '\ufeff';
      csvContent += headers.join(",") + "\n";

      // Group data by ScheduleDate and AreaName
      const grouped = groupByScheduleDateAndArea(filteredData);

      let serial = 1;
      grouped.forEach(group => {
        // For each group, accumulate items
        const groupItems = {};
        group.orders.forEach(order => {
          if (order.itemline && order.itemline.length > 0) {
            order.itemline.forEach(item => {
              const key = `${item.ItemID}`;
              if (!groupItems[key]) {
                groupItems[key] = {
                  itemId: item.ItemID,
                  itemName: item.ItemName,
                  totalQty: 0,
                  createdDates: []
                };
              }
              groupItems[key].totalQty += item.Qty || 0;
              // Collect CreatedDate for each order
              if (order.CreatedDate) {
                groupItems[key].createdDates.push(order.CreatedDate);
              }
            });
          }
        });

        // For each item in group, export a row
        Object.values(groupItems).forEach(item => {
          // If multiple CreatedDates, join by semicolon, else show single
          const createdDateStr = item.createdDates.length > 0
            ? item.createdDates.map(dt => dayjs(dt).format('DD-MM-YYYY HH:mm')).join('; ')
            : '';
          const rowData = [
            serial++,
            group.scheduleDate,
            group.areaName,
            // createdDateStr,
            item.itemId,
            item.itemName,
            item.totalQty
          ];
          csvContent += rowData.map(value => escapeCSV(value)).join(",") + "\n";
        });
      });

      // Create and trigger download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute("download", `horeca_accumulated_items_${fromDate ? fromDate.format('DD-MM-YYYY') : ''}_to_${toDate ? toDate.format('DD-MM-YYYY') : ''}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

    } catch (error) {
      console.error("Error generating CSV:", error);
      alert("Failed to export data. Please try again.");
    } finally {
      setExportLoading(false);
    }
  };

  useEffect(() => {
    fetchSfAreas();
  }, []);

  const handleSubmit = () => {
    if (!selectedSfAreaId || selectedSfAreaId === "") {
      alert('Please select an Area.');
      return;
    }

    if (!fromDate || !toDate) {
      alert('Please select both From Date and To Date.');
      return;
    }

    if (fromDate.isAfter(toDate)) {
      alert('From Date cannot be after To Date.');
      return;
    }

    if (!isDateRangeValid) {
      alert('Date range cannot exceed 75 days. Please select a smaller date range.');
      return;
    }

    fetchLedgerData();
  };

  // Custom styles for date pickers with conditional red border
  const getDatePickerStyle = (isValid = true) => ({
    width: '100%',
    minWidth: '180px',
    '& .MuiInputBase-root': {
      height: '40px',
    },
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: isValid ? '#ccc' : '#f44336', // Red border when invalid
      },
      '&:hover fieldset': {
        borderColor: isValid ? '#999' : '#f44336',
      },
      '&.Mui-focused fieldset': {
        borderColor: isValid ? '#1976d2' : '#f44336',
      },
    },
    '& .MuiInputLabel-root': {
      color: isValid ? 'rgba(0, 0, 0, 0.6)' : '#f44336', // Red label when invalid
    },
  });

  // Legacy date picker style for From Date (always valid styling)
  const datePickerStyle = getDatePickerStyle();
  const groupedOrders = groupByScheduleDateAndArea(filteredData);

  return (
    <>
      <Header />
      <div style={styles.container}>
        {error && <div style={styles.errorMessage}>{error}</div>}
        <div style={styles.inputContainer}>
          <select
            style={styles.dropdown}
            value={selectedSfAreaId}
            onChange={handleSfAreaChange}
          >
            <option value="" disabled>
              Select Area 
            </option>
            <option value="ALL">ALL</option>
            {sfAreas.map((area) => (
              <option key={area.sfAreaId} value={area.sfAreaId}>
                {area.sfAreaName} ({area.sfAreaId})
              </option>
            ))}
          </select>

          <div style={styles.datePickerContainer}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="From Date"
                value={fromDate}
                onChange={(newValue) => {
                  setFromDate(newValue);
                  // Clear data when date changes
                  setLedgerData([]);
                  setFilteredData([]);
                  setAccumulatedItems([]);
                  setHasSubmittedData(false);
                }}
                format="DD-MM-YYYY"
                sx={datePickerStyle}
                slotProps={{
                  textField: {
                    size: "small",
                    variant: "outlined"
                  }
                }}
              />
            </LocalizationProvider>
            {!isDateRangeValid && (
              <div style={{ 
                fontSize: '12px', 
                color: '#f44336', 
                marginTop: '4px',
                fontWeight: '500'
              }}>
                Date range cannot exceed 75 days
              </div>
            )}
          </div>

          <div style={{ margin: '3px' }}>
            <span>-</span>
          </div>

          <div style={styles.datePickerContainer}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="To Date"
                value={toDate}
                onChange={(newValue) => {
                  setToDate(newValue);
                  // Clear data when date changes
                  setLedgerData([]);
                  setFilteredData([]);
                  setAccumulatedItems([]);
                  setHasSubmittedData(false);
                }}
                format="DD-MM-YYYY"
                sx={getDatePickerStyle(isDateRangeValid)}
                slotProps={{
                  textField: {
                    size: "small",
                    variant: "outlined"
                  }
                }}
              />
            </LocalizationProvider>
            {!isDateRangeValid && (
              <div style={{ 
                fontSize: '12px', 
                color: '#f44336', 
                marginTop: '4px',
                fontWeight: '500'
              }}>
                {fromDate && toDate ? 
                  `Range: ${toDate.diff(fromDate, 'day')} days (max 75 days allowed)` :
                  'Date range cannot exceed 75 days'
                }
              </div>
            )}
          </div>

          <button
            onClick={handleSubmit}
            style={{
              ...styles.submitButton,
              opacity: (loading || !selectedSfAreaId ||  !isDateRangeValid) ? 0.6 : 1,
              cursor: (loading || !selectedSfAreaId || !isDateRangeValid) ? 'not-allowed' : 'pointer'
            }}
            disabled={loading || !selectedSfAreaId || !isDateRangeValid}
          >
            {fetchingData ? (
              <>
                <CircularProgress size={14} sx={{ color: 'white', marginRight: '4px' }} />
                Loading...
              </>
            ) : (
              <>
                <SendIcon sx={{ fontSize: 14, marginRight: '4px' }} />
                Submit
              </>
            )}
          </button>
          <button
            onClick={exportToCSV}
            style={{
              ...styles.exportButton,
              opacity: (loading || !hasSubmittedData || accumulatedItems.length === 0 || exportLoading || !isDateRangeValid ) ? 0.6 : 1,
              cursor: (loading || !hasSubmittedData || accumulatedItems.length === 0 || exportLoading || !isDateRangeValid ) ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}
            disabled={loading || !hasSubmittedData || accumulatedItems.length === 0 || exportLoading || !isDateRangeValid }
          >
            {exportLoading ? (
              <>
                <CircularProgress size={14} sx={{ color: 'white' }} />
                Exporting...
              </>
            ) : (
              <>
                <DownloadIcon sx={{ fontSize: 14 }} />
                Export to CSV
              </>
            )}
          </button>
        </div>

        {/* Item Summary Header */}
        {hasSubmittedData && accumulatedItems.length > 0 && (
          <div style={styles.viewToggleContainer}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '16px',
              fontWeight: '600',
              color: '#374151'
            }}>
              <InventoryIcon sx={{ fontSize: 20 }} />
              Item Summary
            </div>
            <div style={{...styles.dataCount, display: 'flex', alignItems: 'center', gap: '4px'}}>
              <AssessmentIcon sx={{ fontSize: 14 }} />
              Total Items: {accumulatedItems.length}
            </div>
          </div>
        )}

        {/* Loading Indicator */}
        {fetchingData && (
          <div style={styles.loaderContainer}>
            <div style={styles.loaderCard}>
              <CircularProgress 
                size={48}
                thickness={4}
                style={styles.loaderSpinner}
                sx={{
                  color: '#3b82f6',
                  '& .MuiCircularProgress-circle': {
                    strokeLinecap: 'round',
                  }
                }}
              />
              <div style={styles.loaderText}>Loading Sale Order Data</div>
              <div style={styles.loaderSubtext}>
                Please wait while we fetch your data...
                <br />
                This may take a few moments.
              </div>
            </div>
          </div>
        )}

        {/* Item Summary Table */}
        {hasSubmittedData && accumulatedItems.length > 0 && (
          <div style={styles.tableContainer}>
            <div style={styles.tableScroll}>
              <table style={styles.table}>
                <thead style={styles.tableHeader}>
                  <tr>
                    <th style={styles.tableHeaderCell}>S No.</th>
                    <th style={styles.tableHeaderCell}>Item ID</th>
                    <th style={styles.tableHeaderCell}>Item Name</th>
                    <th style={styles.tableHeaderCell}>Total Qty</th>
                  </tr>
                </thead>
                <tbody>
                  {accumulatedItems.map((item, index) => (
                    <tr key={item.itemId} style={styles.tableRow}>
                      <td style={styles.tableCell}>{index + 1}</td>
                      <td style={styles.tableCell}>{item.itemId}</td>
                      <td style={styles.tableCell}>{item.itemName || 'Unknown Item'}</td>
                      <td style={styles.tableCell}>{item.totalQty.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* No Data State */}
        {hasSubmittedData && accumulatedItems.length === 0 && (
          <div style={{
            ...styles.tableContainer,
            padding: '40px',
            textAlign: 'center'
          }}>
            <div style={styles.noData}>
              No accumulated item data available for the selected date range
            </div>
          </div>
        )}

        {/* Item Summary Table - Segregated by ScheduleDate and AreaName */}
        {hasSubmittedData && filteredData.length > 0 && (
          <div>
            {groupedOrders.map((group, idx) => {
              // Accumulate items for this group
              const groupItems = {};
              group.orders.forEach(order => {
                if (order.itemline && order.itemline.length > 0) {
                  order.itemline.forEach(item => {
                    if (!groupItems[item.ItemID]) {
                      groupItems[item.ItemID] = {
                        itemId: item.ItemID,
                        itemName: item.ItemName,
                        totalQty: 0
                      };
                    }
                    groupItems[item.ItemID].totalQty += item.Qty || 0;
                  });
                }
              });
              const groupItemsArr = Object.values(groupItems);

              return (
                <div key={group.scheduleDate + group.areaName} style={{
                  ...styles.tableContainer,
                  marginBottom: '32px',
                  border: '2px solid #6366f1',
                  boxShadow: '0 4px 12px -2px #6366f133'
                }}>
                  <div style={{
                    background: '#6366f1',
                    color: '#fff',
                    padding: '10px 18px',
                    fontWeight: 700,
                    fontSize: '15px',
                    borderBottom: '1px solid #e2e8f0',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <span>
                      Schedule Date: <span style={{ color: '#fbbf24' }}>{group.scheduleDate}</span>
                    </span>
                    <span>
                      Area: <span style={{ color: '#fbbf24' }}>{group.areaName}</span>
                    </span>
                    <span style={{ fontWeight: 400, fontSize: '13px' }}>
                      Total Items: {groupItemsArr.length}
                    </span>
                  </div>
                  <div style={styles.tableScroll}>
                    <table style={styles.table}>
                      <thead style={styles.tableHeader}>
                        <tr>
                          <th style={styles.tableHeaderCell}>S No.</th>
                          <th style={styles.tableHeaderCell}>Item ID</th>
                          <th style={styles.tableHeaderCell}>Item Name</th>
                          <th style={styles.tableHeaderCell}>Total Qty</th>
                        </tr>
                      </thead>
                      <tbody>
                        {groupItemsArr.map((item, index) => (
                          <tr key={item.itemId} style={styles.tableRow}>
                            <td style={styles.tableCell}>{index + 1}</td>
                            <td style={styles.tableCell}>{item.itemId}</td>
                            <td style={styles.tableCell}>{item.itemName || 'Unknown Item'}</td>
                            <td style={styles.tableCell}>{item.totalQty.toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
};

export default HorecaAccumulated;